import React, { useEffect, useMemo } from "react";
import { Label } from "design_system";
import { LibrarySelectBox } from "features/libraries";
import { Library } from "helpers/api";
import useLabels from "hooks/useLabels";
import { LIBRARY_SELECT_BOX_ID } from "features/libraries/components/LibrarySelectBox/LibrarySelectBox";

interface LibrarySectionProps {
  selectedLibrary: Library | null;
  onLibraryChange: (library: Library | null) => void;
  matchingLibraries: Library[];
  isDisabled?: boolean;
  isAcademicRole?: boolean;
}

export function LibrarySection({
  selectedLibrary,
  onLibraryChange,
  matchingLibraries,
  isAcademicRole = false,
}: LibrarySectionProps) {
  const [authLabels] = useLabels("auth");
  // Auto-select first matching library when libraries are loaded
  useEffect(() => {
    if (matchingLibraries.length > 0 && !selectedLibrary) {
      onLibraryChange(matchingLibraries[0]);
    }
  }, [matchingLibraries, selectedLibrary, onLibraryChange]);

  // Determine the appropriate label based on role and matching libraries
  const label = useMemo(() => {
    // If there's a matching library
    if (matchingLibraries.length > 0) {
      // For academic roles, show "Confirm your library"
      if (isAcademicRole) {
        return authLabels.onboarding.labels["confirm-your-library"];
      }
      // For non-academic roles, show "Confirm your institution"
      return authLabels.onboarding.labels["confirm-your-institution"];
    }

    // No matching libraries
    // For academic roles, show "Your university"
    if (isAcademicRole) {
      return authLabels.onboarding.labels["your-university"];
    }

    // For other roles (clinician, professional, other), show "Your institution"
    return authLabels.onboarding.labels["your-institution"];
  }, [matchingLibraries.length, isAcademicRole, authLabels]);

  return (
    <div className="w-full flex flex-col gap-2">
      <Label htmlFor={LIBRARY_SELECT_BOX_ID}>{label}</Label>
      <LibrarySelectBox onChange={onLibraryChange} value={selectedLibrary} />
    </div>
  );
}
